/**
 * Tips screen - What to do with your exposure data
 * Evidence-based dietary recommendations for radiation-exposed individuals
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  useColorScheme,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const ACCENT_CYAN = '#00d4ff';
const CARD_BG = '#1e293b';
const CARD_BORDER = '#334155';

export function TipsScreen() {
  const isDark = useColorScheme() === 'dark';
  const theme = isDark
    ? { card: CARD_BG, border: CARD_BORDER, text: '#f1f5f9', muted: '#94a3b8' }
    : { card: '#f8fafc', border: '#e2e8f0', text: '#0f172a', muted: '#64748b' };
  const containerBg = isDark ? '#0f172a' : '#f8fafc';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: containerBg }]} edges={['top']}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          Platform.OS === 'web' && { maxWidth: 480, alignSelf: 'center' as const, width: '100%' },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <Text style={[styles.title, { color: ACCENT_CYAN }]}>What to Do With Your Data</Text>
        <Text style={[styles.subtitle, { color: theme.muted }]}>
          Tracking exposure is useful—but what can you do about it? Research suggests diet may help.
        </Text>

        <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Text style={[styles.cardTitle, { color: theme.text }]}>The Science</Text>
          <Text style={[styles.body, { color: theme.text }]}>
            Multiple studies, including one involving airline pilots exposed to cosmic ionizing radiation, have shown that higher intakes of certain antioxidants from food are associated with reduced markers of DNA damage (e.g., chromosome translocations).
          </Text>
        </View>

        <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Text style={[styles.cardTitle, { color: theme.text }]}>Most Protective Nutrients</Text>
          <Text style={[styles.body, { color: theme.text }]}>
            The most consistent protective associations have been observed with:
          </Text>
          <View style={styles.bulletList}>
            <Text style={[styles.bullet, { color: theme.text }]}>
              • <Text style={styles.bold}>Vitamin C</Text> (ascorbic acid)—particularly from citrus fruits, high-vitamin C vegetables, and green leafy vegetables.
            </Text>
            <Text style={[styles.bullet, { color: theme.text }]}>
              • <Text style={styles.bold}>β-carotene, β-cryptoxanthin, and lutein-zeaxanthin</Text> (carotenoids)—found in carrots, citrus, peppers, dark leafy greens, and other colorful produce.
            </Text>
          </View>
        </View>

        <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Text style={[styles.cardTitle, { color: theme.text }]}>Strongest Effect</Text>
          <Text style={[styles.body, { color: theme.text }]}>
            Combined intakes of vitamins C and E, along with these carotenoids, showed the strongest inverse relationship with DNA damage in radiation-exposed individuals.
          </Text>
        </View>

        <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Text style={[styles.cardTitle, { color: theme.text }]}>Foods to Include</Text>
          <Text style={[styles.body, { color: theme.text }]}>
            Citrus fruits • Bell peppers • Broccoli • Kale & spinach • Carrots • Sweet potatoes • Tomatoes • Berries
          </Text>
        </View>

        <Text style={[styles.disclaimer, { color: theme.muted }]}>
          This is informational only. Consult a healthcare provider for personalized advice. Diet does not replace occupational limits or protective measures.
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { padding: 20, paddingBottom: 40 },
  title: { fontSize: 24, fontWeight: '700', marginBottom: 8 },
  subtitle: { fontSize: 14, marginBottom: 24 },
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  cardTitle: { fontSize: 16, fontWeight: '700', marginBottom: 8 },
  body: { fontSize: 15, lineHeight: 22 },
  bulletList: { marginTop: 8 },
  bullet: { fontSize: 15, lineHeight: 24, marginBottom: 8 },
  bold: { fontWeight: '600' },
  disclaimer: { fontSize: 12, marginTop: 16, fontStyle: 'italic' },
});
